import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UseUrlParamSyncOptions, UseUrlParamSyncReturn } from "../Interfaces/UseUrlParamSync.interfaces";

export const useUrlParamSync = <T extends string = string>({
  paramName = "vb",
  validValues,
  defaultValue
}: UseUrlParamSyncOptions<T>): UseUrlParamSyncReturn<T> => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Refs to prevent circular updates
  const isUpdatingUrl = useRef(false);

  // Use state for isInitialized so consumers can react to the change
  const [isInitialized, setIsInitialized] = useState(false);

  // Get initial value from URL parameter if conditions are met
  const getInitialValue = useCallback((): T => {
    const urlParam = searchParams.get(paramName);
    if (urlParam !== null && validValues.includes(urlParam as T)) {
      return urlParam as T;
    }
    return defaultValue;
  }, [searchParams, paramName, validValues, defaultValue]);

  // Function to update URL parameters
  const updateUrlParam = useCallback(
    (value: T | string) => {
      if (!isUpdatingUrl.current) {
        isUpdatingUrl.current = true;

        const newSearchParams = new URLSearchParams(searchParams);
        if (value && validValues.includes(value as T) && value !== defaultValue) {
          newSearchParams.set(paramName, value);
        } else {
          newSearchParams.delete(paramName);
        }

        setSearchParams(newSearchParams, { replace: true });

        // Reset the flag after a brief delay
        setTimeout(() => {
          isUpdatingUrl.current = false;
        }, 0);
      }
    },
    [searchParams, setSearchParams, paramName, validValues, defaultValue]
  );

  // Sync URL when search params change
  const syncWithUrl = useCallback(
    (currentValue: T): T => {
      if (isInitialized && !isUpdatingUrl.current) {
        // Check if parameter exists in URL (regardless of its value)
        const hasParam = searchParams.has(paramName);
        const urlParam = searchParams.get(paramName);

        if (currentValue === defaultValue) {
          // If current value is default, always remove URL param if it exists
          if (hasParam) {
            updateUrlParam(""); // This will delete the parameter
          }
        } else if (!hasParam && currentValue) {
          // If no URL param but current value is not default, add it
          updateUrlParam(currentValue);
        } else if (urlParam !== null && urlParam !== currentValue && validValues.includes(urlParam as T)) {
          // If URL has different valid value, return URL value
          return urlParam as T;
        }
      }
      return currentValue;
    },
    [searchParams, updateUrlParam, setSearchParams, paramName, validValues, defaultValue, isInitialized]
  );

  // Initialize the hook and trigger re-render when initialized
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
    }
  }, [isInitialized]);

  return {
    syncWithUrl,
    isInitialized,
    updateUrlParam,
    getInitialValue,
    currentValue: getInitialValue()
  };
};
